import { memo, Children } from "react";
import Box from "@mui/material/Box";
import LoadingButton from "@mui/lab/LoadingButton";
import { WebRoutes } from "consts/enpoints/web";
import { when } from "util/support";
import useLogout from "features/auth/useLogout";
import useOnboardingApplication from "features/ecommerce/useOnboardingApplication";
import type { StepperProps } from "./types";
import { useOnboardingStep } from "../../hooks";
import { StepCompletionIndicator, StepRequestedChangeFiledsIndicator } from "./StepIndicators";

const Stepper = ({
    sx,
    slots,
    children,
    route,
    getApplicationModel
}: StepperProps) => {
    const logout = useLogout();

    const { isApplicationSubmitted } = useOnboardingApplication();

    const {
        navigate,
        isCurrentStep,
        getStepModel
    } = useOnboardingStep({
        route,
        getApplicationModel
    });

    const saveAndExit = () => (
        when(
            isApplicationSubmitted,
            () => navigate(WebRoutes.Root),
            logout
        )
    );

    const currentStepSx = {
        bgcolor: 'primary.main',
        fontWeight: 600,
        '*': {
            color: 'primary.contrastText'
        }
    };

    return (
        <Box
            sx={{
                position: 'sticky',
                top: 0,
                bottom: 0,
                py: 6,
                display: 'flex',
                ...sx
            }}
        >
            <Box
                sx={{
                    display: 'grid',
                    gap: 2
                }}
            >
                {slots?.stats}
                <Box>
                    {Children.map(children, (child, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '50px 1fr 50px',
                                alignItems: 'center',
                                gap: 1
                            }}
                        >
                            <StepCompletionIndicator
                                completed={!getStepModel(index + 1)?.isIncomplete}
                            >
                                {index + 1}
                            </StepCompletionIndicator>
                            <Box
                                sx={{
                                    ...when(
                                        isCurrentStep(index + 1),
                                        currentStepSx
                                    ),
                                    px: 2,
                                    py: 1,
                                    borderRadius: 1
                                }}
                            >
                                {child}
                            </Box>
                            <StepRequestedChangeFiledsIndicator
                                hasRequestedChange={getStepModel(index + 1)?.hasRequestedChange}
                            />
                        </Box>
                    ))}
                </Box>
                <LoadingButton
                    variant='outlined'
                    color='inherit'
                    onClick={saveAndExit}
                >
                    Exit & Save Progress
                </LoadingButton>
            </Box>
        </Box>
    );
};

export default memo(Stepper);
